import {loginPath} from "../lib/clinicSignIn";
import React, {useState, useEffect, useRef, useReducer, useMemo} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {verifyUser} from "../authRequests";
import {crud} from "../crudRequests";
import {patientReducer, init} from "../additional_files/reducer";
import Layout from "../components/Layout";
import SharedNotes from "../components/SharedNotes";
import {
  filterFormsByLocation,
  normalizeLocationIds,
} from "../components/FormsStatus";
import {
  Box,
  Flex,
  Text,
  Icon,
  Heading,
  SimpleGrid,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import {
  FiCalendar,
  FiFileText,
  FiMessageCircle,
  FiFolder,
  FiMoon,
  FiChevronRight,
  FiClock,
  FiVideo,
  FiMapPin,
  FiPhone,
  FiCheckCircle,
  FiAlertCircle,
  FiBookOpen,
  FiShield,
  FiActivity,
} from "react-icons/fi";
import {CiPillsBottle1} from "react-icons/ci";
import {deliveryModeLabel, deliveryModeOfAppointment} from "../services/schedule/deliveryMode";
import {isJournalOn} from "../features/journal";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatAppointmentDate(startAt) {
  const d = new Date(startAt);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  if (d.toDateString() === now.toDateString()) return `Today at ${timeStr}`;
  if (d.toDateString() === tomorrow.toDateString())
    return `Tomorrow at ${timeStr}`;
  return `${d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  })} at ${timeStr}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const loadedRef = useRef(null);
  const {state} = useLocation();
  const [prevState, setPrevstate] = useState(state);
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const [pendingForms, setPendingForms] = useState(0);
  const [completedForms, setCompletedForms] = useState(0);
  const [formSubmissions, setFormSubmissions] = useState([]);

  useEffect(function () {
    verifyUser(state ? state.jwt : "", loadedRef);
  }, []);

  useEffect(() => {
    if (!state || !state.patient) {
      navigate(loginPath());
    }
  }, [state, navigate]);

  if (prevState !== state) {
    setPrevstate(state);
    dispatch({type: "UPDATE_STATE", payload: state});
  }

  const portalTabs = dashState?.organization?.patientPortalTabs;
  const canSeePortalTab = (key) => {
    if (!portalTabs) return true;
    return portalTabs[key] !== false;
  };
  const selfBookingHidden = Boolean(
    dashState?.organization?.features?.hidePatientSelfBooking
  );

  useEffect(() => {
    if (!dashState?.patient?.pid || !dashState?.db) return;
    crud(dashState, [
      {
        db: dashState.db,
        collection: "form_submissions",
        // Same filters as the Forms page (FormsStatus). They used to diverge:
        // this counted every submission while the list hid the ones not visible
        // in the portal and the ones from other locations, so Home advertised
        // "22 pending" over an empty list.
        parameters: [{pid: dashState.patient.pid, portalVisible: {$ne: false}}],
        method: "find",
      },
    ])
      .then((res) => {
        const submissions = filterFormsByLocation(
          res?.data?.[0] || [],
          normalizeLocationIds(dashState.patient?.lid)
        );
        const pending = submissions.filter((e) => !e.completed);
        const completed = submissions.filter((e) => e.completed);
        setPendingForms(pending.length);
        setCompletedForms(completed.length);
        setFormSubmissions(submissions);
      })
      .catch(() => {});
  }, [dashState?.patient?.pid]);

  const upcomingAppointments = useMemo(() => {
    const now = Date.now();
    return (dashState?.appointments || [])
      .filter((a) => !a.cancelled && a.startAt > now)
      .sort((a, b) => a.startAt - b.startAt);
  }, [dashState?.appointments]);

  const nextAppointment = upcomingAppointments[0] || null;

  const doctorsMap = useMemo(() => {
    if (dashState.doctor) return {[dashState.doctor.did]: dashState.doctor};
    return (
      dashState.doctors?.reduce((ac, e) => {
        ac[e.did] = e;
        return ac;
      }, {}) || {}
    );
  }, [dashState.doctor, dashState.doctors]);

  const nextDoctor = nextAppointment ? doctorsMap[nextAppointment.did] : null;
  // Always the account holder. A guardian looking at a dependent is on
  // `DependentHome`, which is a different screen with a different data source.
  const patientName = dashState?.patient?.fName || "there";

  const navTo = (route) => {
    navigate(route, {state: {...dashState, firstLoading: true}});
  };

  // Pending forms (most recently assigned first)
  const pendingFormsList = useMemo(() => {
    return formSubmissions
      .filter((e) => !e.completed)
      .sort(
        (a, b) =>
          new Date(b.assignedDate || b.createdAt || 0) -
          new Date(a.assignedDate || a.createdAt || 0)
      )
      .slice(0, 3);
  }, [formSubmissions]);

  return (
    <Layout state={{...dashState}} dispatch={dispatch}>
      {state && (
        <Flex
          ref={loadedRef}
          style={{opacity: 0}}
          flexDirection="column"
          flex={1}
          w="100%"
          overflow="hidden"
        >
          <Box flex={1} overflowY="auto" overflowX="hidden" className="sbar3">
            <Box px={{base: 4, md: 6}} py={5}>
              {/* Header */}
              <Box mb={5}>
                <Heading fontSize="xl" fontWeight="600" color="#1a1f36">
                  {getGreeting()}, {patientName}
                </Heading>
                <Text fontSize="13px" color="#6b7280" mt={1}>
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Text>
              </Box>

              {/* Anything the clinic deliberately showed you. Renders nothing
                  at all when there is nothing — it is a message, not a section,
                  so an empty state here would be an empty state about nothing. */}
              <SharedNotes state={state} />

              {/* Next Appointment — prominent card */}
              <Box
                bg="white"
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="lg"
                p={5}
                mb={4}
                cursor="pointer"
                onClick={() => navTo("../appointments")}
                transition="all 0.15s"
                _hover={{borderColor: "gray.200", boxShadow: "sm"}}
              >
                <Flex align="center" justify="space-between" mb={3}>
                  <HStack spacing={2}>
                    <Flex
                      w="28px"
                      h="28px"
                      bg="#f0f0ff"
                      rounded="lg"
                      align="center"
                      justify="center"
                    >
                      <Icon as={FiCalendar} color="#5754FF" fontSize="13px" />
                    </Flex>
                    <Text fontSize="sm" fontWeight="600" color="#1a1f36">
                      Next appointment
                    </Text>
                  </HStack>
                  <HStack spacing={1}>
                    {upcomingAppointments.length > 1 && (
                      <Text fontSize="xs" color="gray.400">
                        +{upcomingAppointments.length - 1} more
                      </Text>
                    )}
                    <Icon as={FiChevronRight} color="gray.400" fontSize="14px" />
                  </HStack>
                </Flex>
                {nextAppointment ? (
                  <Flex align="center" gap={4} flexWrap="wrap">
                    <HStack spacing={1.5}>
                      <Icon as={FiClock} color="gray.400" fontSize="13px" />
                      <Text fontSize="sm" color="#1a1f36" fontWeight="500">
                        {formatAppointmentDate(nextAppointment.startAt)}
                      </Text>
                    </HStack>
                    {nextDoctor && (
                      <HStack spacing={1.5}>
                        <Box
                          w="6px"
                          h="6px"
                          bg="#5754FF"
                          rounded="full"
                          flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.600">
                          Clinician {nextDoctor.lastName || nextDoctor.firstName}
                        </Text>
                      </HStack>
                    )}
                    {deliveryModeOfAppointment(nextAppointment) === "videoconference" && (
                      <HStack spacing={1.5}>
                        <Icon as={FiVideo} color="gray.400" fontSize="13px" />
                        <Text fontSize="xs" color="gray.500">
                          Video visit
                        </Text>
                      </HStack>
                    )}
                  </Flex>
                ) : (
                  <Text fontSize="sm" color="gray.400">
                    No upcoming appointments
                    {/* Second self-booking entry point after the Appointments
                        Book button. Without this guard the org could hide Book
                        and still invite the patient to schedule, landing them on
                        a page with no way to do it. */}
                    {!selfBookingHidden && (
                      <>
                        {" — "}
                        <Text as="span" color="#5754FF" fontWeight="500">
                          schedule one
                        </Text>
                      </>
                    )}
                  </Text>
                )}
              </Box>

              {/* Quick stats row */}
              <SimpleGrid columns={{base: 2, md: 4}} spacing={4} mb={4}>
                {canSeePortalTab("forms") && (
                  <StatCard
                    icon={FiFileText}
                    iconColor={pendingForms > 0 ? "#e8a23e" : "#34a853"}
                    iconBg={pendingForms > 0 ? "#fef7ec" : "#eefbf1"}
                    label="Forms"
                    value={
                      pendingForms > 0
                        ? `${pendingForms} pending`
                        : "All complete"
                    }
                    onClick={() => navTo("../forms")}
                  />
                )}
                {canSeePortalTab("messaging") && (
                  <StatCard
                    icon={FiMessageCircle}
                    iconColor="#5754FF"
                    iconBg="#f0f0ff"
                    label="Messages"
                    value="Open inbox"
                    onClick={() => navTo("../chat")}
                  />
                )}
                {canSeePortalTab("sleepDiary") && (
                  <StatCard
                    icon={FiMoon}
                    iconColor="#7c6dd8"
                    iconBg="#f3f0ff"
                    label="Sleep Diary"
                    value="Log today"
                    onClick={() => navTo("../sleep")}
                  />
                )}
                {canSeePortalTab("prescriptions") && (
                  <StatCard
                    icon={CiPillsBottle1}
                    iconColor="#3b82f6"
                    iconBg="#eff6ff"
                    label="Prescriptions"
                    value="Log compliance"
                    onClick={() => navTo("../prescriptionCompliance")}
                  />
                )}
              </SimpleGrid>

              {/* Two column layout */}
              <SimpleGrid columns={{base: 1, lg: 2}} spacing={4} mb={4}>
                {/* Pending forms */}
                <Box
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.100"
                  borderRadius="lg"
                  p={5}
                >
                  <Flex align="center" justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="600" color="#1a1f36">
                      Action items
                    </Text>
                    {pendingForms > 0 && (
                      <Flex
                        px={2}
                        py={0.5}
                        bg="#fef7ec"
                        rounded="full"
                      >
                        <Text fontSize="10px" fontWeight="600" color="#e8a23e">
                          {pendingForms}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                  <VStack spacing={0} align="stretch">
                    {pendingFormsList.length > 0 ? (
                      pendingFormsList.map((form, i) => (
                        <Flex
                          key={i}
                          align="center"
                          justify="space-between"
                          py={2.5}
                          borderBottomWidth={i < pendingFormsList.length - 1 ? "1px" : 0}
                          borderColor="gray.50"
                          cursor="pointer"
                          onClick={() => navTo("../forms")}
                          _hover={{bg: "gray.25"}}
                          mx={-2}
                          px={2}
                          rounded="md"
                        >
                          <HStack spacing={2.5}>
                            <Icon
                              as={FiAlertCircle}
                              color="#e8a23e"
                              fontSize="14px"
                            />
                            <Box>
                              <Text fontSize="13px" color="#1a1f36" fontWeight="500">
                                {/* `formTitle` is the field. `formName` is not one — these
                                    documents come straight out of `form_submissions`,
                                    which the Forms page and the dependent Forms screen
                                    both read as `formTitle`. So this fell through to the
                                    fallback for every patient, every time: Rafa's home
                                    screen listed "Form to complete" three times over,
                                    identical but for the date, with 336 behind them. */}
                                {form.formTitle || form.formName || "Form to complete"}
                              </Text>
                              <Text fontSize="11px" color="gray.400">
                                Assigned{" "}
                                {new Date(
                                  form.assignedDate || form.createdAt || Date.now()
                                ).toLocaleDateString(
                                  "en-US",
                                  {month: "short", day: "numeric"}
                                )}
                              </Text>
                            </Box>
                          </HStack>
                          <Icon as={FiChevronRight} color="gray.300" fontSize="14px" />
                        </Flex>
                      ))
                    ) : (
                      <Flex align="center" gap={2} py={3}>
                        <Icon as={FiCheckCircle} color="#34a853" fontSize="14px" />
                        <Text fontSize="13px" color="gray.500">
                          You're all caught up
                        </Text>
                      </Flex>
                    )}
                  </VStack>
                </Box>

                {/* Quick links */}
                <Box
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.100"
                  borderRadius="lg"
                  p={5}
                >
                  <Text fontSize="sm" fontWeight="600" color="#1a1f36" mb={3}>
                    Quick access
                  </Text>
                  <VStack spacing={0} align="stretch">
                    {canSeePortalTab("journal") && isJournalOn(dashState) && (
                      <QuickLink
                        icon={FiBookOpen}
                        iconColor="#5754FF"
                        label="Journal"
                        desc="Write in your daily journal"
                        onClick={() => navTo("../journal")}
                      />
                    )}
                    {canSeePortalTab("files") && (
                      <QuickLink
                        icon={FiFolder}
                        iconColor="#6b7280"
                        label="Files"
                        desc="View shared documents"
                        onClick={() => navTo("../files")}
                      />
                    )}
                    <QuickLink
                      icon={FiShield}
                      iconColor="#6b7280"
                      label="Security"
                      desc="Password and account settings"
                      onClick={() => navTo("../security")}
                      isLast
                    />
                  </VStack>
                </Box>
              </SimpleGrid>

              {/* Upcoming appointments list */}
              {upcomingAppointments.length > 1 && (
                <Box
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.100"
                  borderRadius="lg"
                  p={5}
                >
                  <Flex align="center" justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="600" color="#1a1f36">
                      Upcoming appointments
                    </Text>
                    <Text
                      fontSize="xs"
                      color="#5754FF"
                      fontWeight="500"
                      cursor="pointer"
                      onClick={() => navTo("../appointments")}
                      _hover={{textDecoration: "underline"}}
                    >
                      View all
                    </Text>
                  </Flex>
                  <VStack spacing={0} align="stretch">
                    {upcomingAppointments.slice(0, 5).map((appt, i) => {
                      const doc = doctorsMap[appt.did];
                      return (
                        <Flex
                          key={i}
                          align="center"
                          justify="space-between"
                          py={2.5}
                          borderBottomWidth={
                            i < Math.min(upcomingAppointments.length, 5) - 1
                              ? "1px"
                              : 0
                          }
                          borderColor="gray.50"
                        >
                          <HStack spacing={3}>
                            <Flex
                              w="36px"
                              h="36px"
                              bg="gray.50"
                              rounded="lg"
                              align="center"
                              justify="center"
                              flexShrink={0}
                            >
                              <Text fontSize="xs" fontWeight="600" color="gray.600">
                                {new Date(appt.startAt).getDate()}
                              </Text>
                            </Flex>
                            <Box>
                              <Text fontSize="13px" color="#1a1f36" fontWeight="500">
                                {formatAppointmentDate(appt.startAt)}
                              </Text>
                              <Text fontSize="11px" color="gray.400">
                                {doc
                                  ? `Clinician ${doc.lastName || doc.firstName}`
                                  : "Clinician"}
                                {` · ${deliveryModeLabel(deliveryModeOfAppointment(appt) || "in-person")}`}
                              </Text>
                            </Box>
                          </HStack>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </Box>
          </Box>
        </Flex>
      )}
    </Layout>
  );
}

function StatCard({icon, iconColor, iconBg, label, value, onClick}) {
  return (
    <Flex
      bg="white"
      borderWidth="1px"
      borderColor="gray.100"
      borderRadius="lg"
      p={4}
      direction="column"
      cursor="pointer"
      onClick={onClick}
      transition="all 0.15s"
      _hover={{borderColor: "gray.200", boxShadow: "sm"}}
    >
      <Flex
        w="28px"
        h="28px"
        bg={iconBg}
        rounded="lg"
        align="center"
        justify="center"
        mb={3}
      >
        <Icon as={icon} color={iconColor} fontSize="13px" />
      </Flex>
      <Text fontSize="xs" color="gray.400" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="13px" fontWeight="500" color="#1a1f36">
        {value}
      </Text>
    </Flex>
  );
}

function QuickLink({icon, iconColor, label, desc, onClick, isLast}) {
  return (
    <Flex
      align="center"
      justify="space-between"
      py={2.5}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor="gray.50"
      cursor="pointer"
      onClick={onClick}
      _hover={{bg: "gray.25"}}
      mx={-2}
      px={2}
      rounded="md"
    >
      <HStack spacing={3}>
        <Flex
          w="28px"
          h="28px"
          bg="gray.50"
          rounded="lg"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} color={iconColor} fontSize="13px" />
        </Flex>
        <Box>
          <Text fontSize="13px" color="#1a1f36" fontWeight="500">
            {label}
          </Text>
          <Text fontSize="11px" color="gray.400">
            {desc}
          </Text>
        </Box>
      </HStack>
      <Icon as={FiChevronRight} color="gray.300" fontSize="14px" />
    </Flex>
  );
}
